import React, { useState, useEffect } from 'react';
import api from '../utils/api';

export default function SmtpSettingsPage() {
  const [form, setForm] = useState({
    smtp_host: '', smtp_port: '587', smtp_user: '',
    smtp_pass: '', smtp_from: '', app_url: '',
  });
  const [notif, setNotif] = useState({
    notifications_enabled:      'true',
    reminder_enabled:           'true',
    assignment_email:           'true',
    comment_email:              'true',
    payment_email:              'true',
    phase_access_email:         'true',
    deadline_email:             'true',
    deadline_7day_email:        'true',
    deadline_3day_email:        'true',
    phase_completion_email:     'true',
    weekly_summary_email:       'false',
    weekly_summary_day:         '1',
    weekly_summary_time:        '08:00',
    milestone_task_reopen_email:  'true',
  });
  const [timezone, setTimezone] = React.useState('Asia/Dubai');
  const [savingTz, setSavingTz] = React.useState(false);
  const [savedTz,  setSavedTz]  = React.useState(false);
  const [savingNotif, setSavingNotif] = useState(false);
  const [savedNotif, setSavedNotif]   = useState(false);
  const [testingWeekly, setTestingWeekly] = useState(false);
  const [weeklyTestMsg, setWeeklyTestMsg] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [testMsg,  setTestMsg]  = useState('');
  const [testErr,  setTestErr]  = useState('');
  const [error,    setError]    = useState('');

  useEffect(() => {
    api.get('/settings/smtp').then(data => {
      setForm(f => ({ ...f, ...data }));
    }).catch(() => {}).finally(() => setLoading(false));
    api.get('/settings/notifications').then(data => {
      setNotif(n => ({ ...n, ...data }));
      if (data.app_timezone) setTimezone(data.app_timezone);
    }).catch(() => {});
  }, []);

  async function save() {
    setSaving(true); setSaved(false); setError('');
    try {
      await api.put('/settings/smtp', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e) {
      setError(e?.error || 'Failed to save');
    } finally { setSaving(false); }
  }

  async function testEmail() {
    setTesting(true); setTestMsg(''); setTestErr('');
    try {
      const res = await api.post('/settings/smtp/test', {});
      setTestMsg(res.message || 'Test email sent successfully!');
    } catch(e) {
      setTestErr(e?.error || 'Test failed');
    } finally { setTesting(false); }
  }

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  if (loading) return (
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)'}}>
      <div style={{width:20,height:20,border:'2.5px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
    </div>
  );

  return (
    <div style={{flex:1,overflowY:'auto',background:'var(--bg)',padding:'28px'}}>
      <div style={{maxWidth:'600px',margin:'0 auto'}}>
        <h2 style={{fontSize:'20px',fontWeight:'800',color:'var(--text)',marginBottom:'6px'}}>⚙️ Email & Notification Settings</h2>
        <p style={{fontSize:'13px',color:'var(--muted)',marginBottom:'28px',lineHeight:1.6}}>
          Configure the SMTP server used for escalation emails, task assignments, comments, and password resets.
          Settings saved here override the .env file.
        </p>

        {error && (
          <div style={{background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.25)',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',color:'#DC2626'}}>
            ⚠️ {error}
          </div>
        )}

        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'14px',padding:'24px',display:'flex',flexDirection:'column',gap:'16px'}}>

          <div style={{display:'grid',gridTemplateColumns:'1fr 120px',gap:'12px'}}>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">SMTP Host</label>
              <input className="input" value={form.smtp_host} onChange={e=>f('smtp_host',e.target.value)}
                placeholder="e.g. smtp.office365.com"/>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Port</label>
              <input className="input" value={form.smtp_port} onChange={e=>f('smtp_port',e.target.value)}
                placeholder="587" type="number"/>
            </div>
          </div>

          <div className="form-group" style={{margin:0}}>
            <label className="form-label">SMTP Username (email address)</label>
            <input className="input" value={form.smtp_user} onChange={e=>f('smtp_user',e.target.value)}
              placeholder="e.g. notifications@yourcompany.com" type="email"/>
          </div>

          <div className="form-group" style={{margin:0}}>
            <label className="form-label">SMTP Password</label>
            <input className="input" value={form.smtp_pass} onChange={e=>f('smtp_pass',e.target.value)}
              placeholder="Leave blank to keep existing password" type="password"/>
            <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'4px'}}>
              The current password is hidden. Only enter a value if you want to change it.
            </div>
          </div>

          <div className="form-group" style={{margin:0}}>
            <label className="form-label">From Address</label>
            <input className="input" value={form.smtp_from} onChange={e=>f('smtp_from',e.target.value)}
              placeholder="e.g. EOMP <notifications@yourcompany.com>" type="email"/>
          </div>

          <div className="form-group" style={{margin:0}}>
            <label className="form-label">App URL <span style={{fontSize:'11px',fontWeight:'400',color:'var(--muted)'}}>used in email links</span></label>
            <input className="input" value={form.app_url} onChange={e=>f('app_url',e.target.value)}
              placeholder="e.g. https://trajectory.yourcompany.com"/>
          </div>

          {/* Provider hints */}
          <div style={{background:'var(--surface2)',borderRadius:'8px',padding:'12px 14px',fontSize:'12px',color:'var(--muted)'}}>
            <strong style={{color:'var(--text)'}}>Common SMTP Settings:</strong><br/>
            Office 365: <code>smtp.office365.com</code> · Port 587 · Use full email as username<br/>
            Gmail: <code>smtp.gmail.com</code> · Port 587 · Requires App Password (not regular password)<br/>
            Any provider: Contact your IT team for the SMTP server address
          </div>

          <div style={{display:'flex',gap:'10px',paddingTop:'8px',borderTop:'1px solid var(--border)'}}>
            <button onClick={save} disabled={saving}
              className="btn btn-primary"
              style={{padding:'10px 24px',fontWeight:'700',background:saved?'#16A34A':undefined,transition:'background .3s'}}>
              {saving
                ? <span style={{display:'flex',alignItems:'center',gap:'6px'}}><span style={{width:12,height:12,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'spin .7s linear infinite'}}/>Saving…</span>
                : saved ? <span style={{display:'flex',alignItems:'center',gap:'6px'}}>✅ SMTP Settings Saved</span>
                : 'Save SMTP Settings'}
            </button>
            <button onClick={testEmail} disabled={testing || saving}
              className="btn"
              style={{padding:'10px 18px'}}>
              {testing ? 'Sending…' : '📧 Send Test Email'}
            </button>
          </div>

          {testMsg && (
            <div style={{background:'rgba(22,163,74,0.08)',border:'1px solid rgba(22,163,74,0.3)',borderRadius:'8px',padding:'10px 14px',fontSize:'13px',color:'#16A34A'}}>
              ✅ {testMsg}
            </div>
          )}
          {testErr && (
            <div style={{background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.25)',borderRadius:'8px',padding:'10px 14px',fontSize:'13px',color:'#DC2626'}}>
              ❌ {testErr}
            </div>
          )}
        </div>

        {/* ── Timezone Configuration ───────────────────────────────── */}
        <h2 style={{fontSize:'18px',fontWeight:'800',color:'var(--text)',margin:'28px 0 6px'}}>🌍 Timezone Configuration</h2>
        <p style={{fontSize:'13px',color:'var(--muted)',marginBottom:'16px'}}>Controls timestamps in audit logs, email notifications, and scheduled reminders.</p>
        <div className="card" style={{padding:'20px',marginBottom:'10px'}}>
          <label className="form-label">Application Timezone</label>
          <select value={timezone} onChange={e=>setTimezone(e.target.value)} className="form-control" style={{maxWidth:'320px'}}>
            {['Pacific/Auckland','Australia/Sydney','Asia/Tokyo','Asia/Singapore','Asia/Dubai','Asia/Karachi','Asia/Kolkata','Europe/Moscow','Africa/Nairobi','Europe/Istanbul','Europe/Athens','Europe/Berlin','Europe/London','UTC','Atlantic/Azores','America/New_York','America/Chicago','America/Denver','America/Los_Angeles'].map(tz=>(<option key={tz} value={tz}>{tz}</option>))}
          </select>
          <div style={{marginTop:'14px',display:'flex',alignItems:'center',gap:'10px'}}>
            <button className="btn btn-primary" onClick={async()=>{setSavingTz(true);setSavedTz(false);try{await api.put('/settings/notifications',{...notif,app_timezone:timezone});setSavedTz(true);setTimeout(()=>setSavedTz(false),3000);}catch(e){}finally{setSavingTz(false);}}}>{savingTz?'Saving…':savedTz?'✅ Timezone Saved':'Save Timezone'}</button>
          </div>
        </div>

        {/* ── Notification Controls ─────────────────────────────────── */}
        <h2 style={{fontSize:'18px',fontWeight:'800',color:'var(--text)',margin:'28px 0 6px'}}>🔔 Notification Controls</h2>
        <p style={{fontSize:'13px',color:'var(--muted)',marginBottom:'16px',lineHeight:1.6}}>
          Turn off any notification type immediately. Changes take effect for all users at once.
        </p>
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'14px',padding:'20px',display:'flex',flexDirection:'column',gap:'0'}}>

          {/* Master kill-switch */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 0',borderBottom:'1px solid var(--border)'}}>
            <div>
              <div style={{fontSize:'14px',fontWeight:'700',color:'var(--text)'}}>All Email Notifications</div>
              <div style={{fontSize:'12px',color:'var(--muted)',marginTop:'2px'}}>Master switch — overrides all settings below</div>
            </div>
            <label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer'}}>
              <div onClick={()=>setNotif(n=>({...n,notifications_enabled:n.notifications_enabled==='true'?'false':'true'}))}
                style={{width:44,height:24,borderRadius:12,background:notif.notifications_enabled==='true'?'#16A34A':'#D1D5DB',transition:'background .2s',display:'flex',alignItems:'center',padding:'2px',cursor:'pointer'}}>
                <div style={{width:20,height:20,borderRadius:'50%',background:'white',transition:'transform .2s',transform:notif.notifications_enabled==='true'?'translateX(20px)':'translateX(0)'}}/>
              </div>
              <span style={{fontSize:'12px',fontWeight:'700',color:notif.notifications_enabled==='true'?'#16A34A':'var(--muted)'}}>
                {notif.notifications_enabled==='true'?'ON':'OFF'}
              </span>
            </label>
          </div>

          {/* Individual controls */}
          {[
            { key:'assignment_email',   label:'Task Assignment Emails',      desc:'Sent when a user is assigned to a task' },
            { key:'comment_email',      label:'Comment Notification Emails',  desc:'Sent to task owner when someone comments on their task' },
            { key:'reminder_enabled',   label:'Overdue Task Reminders',       desc:'Sent daily for high-priority overdue tasks' },
            { key:'payment_email',      label:'Payment Milestone Emails',     desc:'Sent to project managers when a payment milestone is released' },
            { key:'phase_access_email', label:'Phase Access Granted Emails',  desc:'Sent to a user when they are given access to a private phase' },
            { key:'deadline_email',         label:'Due Tomorrow Reminder',            desc:'Daily email for tasks due the next day' },
            { key:'deadline_3day_email',     label:'3 Days Before Due Date',           desc:'Reminder sent 3 days before a task is due' },
            { key:'deadline_7day_email',     label:'7 Days Before Due Date',           desc:'Early reminder sent one week before a task is due' },
            { key:'phase_completion_email',  label:'Phase Completion Notification',    desc:'Sent to project managers when all lists in a phase are 100% complete' },
            { key:'weekly_summary_email',    label:'Weekly Project Summary',           desc:'Every Monday morning: project progress snapshot for project managers' },
            { key:'milestone_task_reopen_email', label:'Released Milestone Under Review', desc:'Email alert when a task is re-opened after its payment milestone was released' },
          ].map(item => (
            <div key={item.key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 0',borderBottom:'1px solid var(--border)',opacity:notif.notifications_enabled==='false'?0.4:1}}>
              <div>
                <div style={{fontSize:'13px',fontWeight:'600',color:'var(--text)'}}>{item.label}</div>
                <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'1px'}}>{item.desc}</div>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer'}}>
                <div onClick={()=>{if(notif.notifications_enabled==='false')return;setNotif(n=>({...n,[item.key]:n[item.key]==='true'?'false':'true'}));}}
                  style={{width:44,height:24,borderRadius:12,background:notif[item.key]==='true'&&notif.notifications_enabled!=='false'?'#5B5BD6':'#D1D5DB',transition:'background .2s',display:'flex',alignItems:'center',padding:'2px',cursor:notif.notifications_enabled==='false'?'not-allowed':'pointer'}}>
                  <div style={{width:20,height:20,borderRadius:'50%',background:'white',transition:'transform .2s',transform:notif[item.key]==='true'?'translateX(20px)':'translateX(0)'}}/>
                </div>
                <span style={{fontSize:'12px',fontWeight:'700',color:notif[item.key]==='true'&&notif.notifications_enabled!=='false'?'#5B5BD6':'var(--muted)'}}>
                  {notif[item.key]==='true'&&notif.notifications_enabled!=='false'?'ON':'OFF'}
                </span>
              </label>
            </div>
          ))}

          {notif.weekly_summary_email==='true' && notif.notifications_enabled!=='false' && (
            <div style={{padding:'12px 16px',background:'var(--surface2)',borderRadius:'10px',marginTop:'14px',border:'1px solid var(--border)',fontSize:'12px',color:'var(--muted)'}}>
              📅 Weekly summaries are scheduled <strong>per project</strong>. Open a project's Dashboard → <strong>Weekly Summary</strong> to set its day, time, and send a test. This master switch must stay ON for project schedules to run.
            </div>
          )}

          <div style={{paddingTop:'14px',display:'flex',justifyContent:'flex-end'}}>
            <button onClick={async()=>{
              setSavingNotif(true); setSavedNotif(false);
              try {
                await api.put('/settings/notifications', notif);
                setSavedNotif(true);
                setTimeout(()=>setSavedNotif(false), 3500);
              } catch(e) { setError(e?.error||'Failed to save notification settings'); }
              finally { setSavingNotif(false); }
            }} disabled={savingNotif} className="btn btn-primary"
              style={{padding:'10px 24px',fontWeight:'700',background:savedNotif?'#16A34A':undefined,transition:'background .3s'}}>
              {savingNotif
                ? <span style={{display:'flex',alignItems:'center',gap:'6px'}}><span style={{width:12,height:12,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'spin .7s linear infinite'}}/>Saving…</span>
                : savedNotif
                ? <span style={{display:'flex',alignItems:'center',gap:'6px'}}>✅ Notification Settings Saved</span>
                : 'Save Notification Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
